import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { UNIVERSITIES, COUNTRIES } from "../data";
import "./UniversitiesPage.css";

export default function UniversitiesPage() {
  const { country } = useParams();

  // ---------------- STATE ----------------
  const [search, setSearch] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(country || "");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedDuration, setSelectedDuration] = useState("");
  const [selectedIELTS, setSelectedIELTS] = useState("");
  const [selectedRanking, setSelectedRanking] = useState("");
  const [sortBy, setSortBy] = useState("ranking");

  // ---------------- UNIVERSITIES ----------------
  const universities = useMemo(() => {
    let list = Object.values(UNIVERSITIES);

    // COUNTRY
    if (selectedCountry) {
      list = list.filter((u) => u.country === selectedCountry);
    }

    // SEARCH
    if (search.trim()) {
      list = list.filter((u) =>
        u.name.toLowerCase().includes(search.toLowerCase())
      );
    }

    // COURSE
    if (selectedCourse) {
      list = list.filter((u) =>
        u.topCourses.some((c) =>
          c.name.toLowerCase().includes(selectedCourse.toLowerCase())
        )
      );
    }

    // DURATION
    if (selectedDuration) {
      list = list.filter((u) =>
        u.topCourses.some((c) =>
          c.duration.includes(selectedDuration)
        )
      );
    }

    // IELTS
    if (selectedIELTS) {
      list = list.filter((u) => {
        const score = parseFloat(u.ielts);
        return score <= parseFloat(selectedIELTS);
      });
    }

    // QS RANK
    if (selectedRanking) {
      list = list.filter((u) => {
        const rank =
          parseInt(u.qsRank.match(/\d+/)?.[0]) || 9999;

        if (selectedRanking === "50") return rank <= 50;
        if (selectedRanking === "100") return rank <= 100;
        if (selectedRanking === "250") return rank <= 250;

        return true;
      });
    }

    // SORTING
    if (sortBy === "ranking") {
      list.sort((a, b) => {
        const rankA =
          parseInt(a.qsRank.match(/\d+/)?.[0]) || 9999;

        const rankB =
          parseInt(b.qsRank.match(/\d+/)?.[0]) || 9999;

        return rankA - rankB;
      });
    }

    if (sortBy === "name") {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }

    return list;
  }, [
    selectedCountry,
    search,
    selectedCourse,
    selectedDuration,
    selectedIELTS,
    selectedRanking,
    sortBy,
  ]);

  return (
    <div className="universities-page">

      {/* HERO */}
      <section className="upage-hero">

        <div className="upage-hero-overlay" />

        <div className="upage-hero-content">

          <span className="upage-badge">
            🎓 Explore Global Universities
          </span>

          <h1 className="upage-title">
            Find Your Perfect University
          </h1>

          <p className="upage-subtitle">
            Discover world-class universities across Germany,
            USA, UK, Canada & Australia.
          </p>

          {/* SEARCH */}
          <div className="upage-search">
            <input
              type="text"
              placeholder="Search universities..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

        </div>
      </section>

      {/* MAIN */}
      <section className="upage-main">

        <div className="upage-container">

          {/* SIDEBAR */}
          <aside className="upage-sidebar">

            <div className="upage-filter-card">

              <div className="upage-filter-top">
                <h3>Filters</h3>

                <button
                  onClick={() => {
                    setSelectedCountry("");
                    setSelectedCourse("");
                    setSelectedDuration("");
                    setSelectedIELTS("");
                    setSelectedRanking("");
                    setSearch("");
                  }}
                >
                  Reset
                </button>
              </div>

              {/* COUNTRY */}
              <div className="upage-filter-group">
                <label>Country</label>

                <select
                  value={selectedCountry}
                  onChange={(e) =>
                    setSelectedCountry(e.target.value)
                  }
                >
                  <option value="">All Countries</option>

                  {Object.values(COUNTRIES).map((c) => (
                    <option key={c.slug} value={c.slug}>
                      {c.flag} {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* COURSE */}
              <div className="upage-filter-group">
                <label>Course</label>

                <select
                  value={selectedCourse}
                  onChange={(e) =>
                    setSelectedCourse(e.target.value)
                  }
                >
                  <option value="">All Courses</option>

                  <option value="computer">
                    Computer Science
                  </option>

                  <option value="data">
                    Data Science
                  </option>

                  <option value="mba">
                    MBA
                  </option>

                  <option value="engineering">
                    Engineering
                  </option>

                  <option value="civil">
                    Civil Engineering
                  </option>

                  <option value="mechanical">
                    Mechanical Engineering
                  </option>
                </select>
              </div>

              {/* DURATION */}
              <div className="upage-filter-group">
                <label>Duration</label>

                <select
                  value={selectedDuration}
                  onChange={(e) =>
                    setSelectedDuration(e.target.value)
                  }
                >
                  <option value="">Any Duration</option>

                  <option value="1">
                    1 Year
                  </option>

                  <option value="2">
                    2 Years
                  </option>

                  <option value="3">
                    3 Years
                  </option>
                </select>
              </div>

              {/* RANKING */}
              <div className="upage-filter-group">
                <label>QS Ranking</label>

                <select
                  value={selectedRanking}
                  onChange={(e) =>
                    setSelectedRanking(e.target.value)
                  }
                >
                  <option value="">All Rankings</option>

                  <option value="50">
                    Top 50
                  </option>

                  <option value="100">
                    Top 100
                  </option>

                  <option value="250">
                    Top 250
                  </option>
                </select>
              </div>

            </div>

          </aside>

          {/* CONTENT */}
          <div className="upage-content">

            {/* TOPBAR */}
            <div className="upage-topbar">

              <div>
                <h2>
                  {universities.length} Universities Found
                </h2>

                <p>
                  Explore universities tailored to your goals
                </p>
              </div>

              {/* SORT */}
              <div className="upage-sort">

                <select
                  value={sortBy}
                  onChange={(e) =>
                    setSortBy(e.target.value)
                  }
                >
                  <option value="ranking">
                    Sort by Ranking
                  </option>

                  <option value="name">
                    Sort by Name
                  </option>
                </select>

              </div>

            </div>

            {/* GRID */}
            <div className="upage-grid">

              {universities.map((uni) => (

                <Link
                  key={uni.slug}
                  to={`/university/${uni.slug}`}
                  className="upage-card"
                >

                  {/* IMAGE */}
                  <div className="upage-card-image-wrap">

                    <img
                      src={uni.heroImg ||uni.campusImg}
                      alt={uni.name}
                      className="upage-card-image"
                    />

                    <div className="upage-card-overlay" />

                    <div className="upage-card-rank">
                      {uni.qsRank}
                    </div>

                  </div>

                  {/* CONTENT */}
                  <div className="upage-card-content">

                    <div className="upage-card-country">
                      {uni.flag} {COUNTRIES[uni.country]?.name}
                    </div>

                    <h3 className="upage-card-name">
                      {uni.name}
                    </h3>

                    <p className="upage-card-city">
                      📍 {uni.city}
                    </p>

                    <p className="upage-card-overview">
                      {uni.overview.substring(0, 110)}...
                    </p>

                    {/* COURSES */}
                    <div className="upage-course-tags">

                      {uni.topCourses
                        .slice(0, 3)
                        .map((course, i) => (
                          <span key={i}>
                            {course.name}
                          </span>
                        ))}

                    </div>

                    {/* STATS */}
                    <div className="upage-card-stats">

                      <div>
                        <span>IELTS</span>
                        <strong>{uni.ielts}</strong>
                      </div>

                      <div>
                        <span>Tuition</span>
                        <strong>
                          {uni.tuition.international}
                        </strong>
                      </div>

                    </div>

                    {/* FOOTER */}
                    <div className="upage-card-footer">

                      <span>
                        🎓 {uni.placementStats.rate}
                      </span>

                      <span className="upage-view">
                        View →
                      </span>

                    </div>

                  </div>

                </Link>

              ))}

            </div>

          </div>

        </div>

      </section>

    </div>
  );
}